import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  KeyValuePairs,
  Link,
  SpaceBetween,
  StatusIndicator,
  StatusIndicatorProps,
  Table,
  TextFilter,
} from '@cloudscape-design/components';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import {
  cancelChainSearch,
  createChainScorecards,
  getChainSearch,
  retryChainSearch,
  selectChainCandidates,
  type ChainSearchCandidate,
} from '../../../common/api/chains';
import { DashboardViewLayout } from '../../../components/DashboardViewLayout';
import { PlaceStatus } from '../../../components/PlaceStatus';
import { PlacePeriodList } from '../../../components/PlacePeriodList';

const TERMINAL = new Set(['complete', 'failed', 'cancelled']);

const STATUS_INDICATOR: Record<string, StatusIndicatorProps.Type> = {
  pending: 'pending',
  discovering: 'in-progress',
  review: 'info',
  creating: 'in-progress',
  complete: 'success',
  failed: 'error',
  cancelled: 'stopped',
};

export default function ChainSearchDetailPage() {
  const { uuid } = useParams({ from: '/dashboard/chains_/searches/$uuid' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [filteringText, setFilteringText] = useState('');
  const [localSelection, setLocalSelection] = useState<Set<string> | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['chain-search', uuid],
    queryFn: () => getChainSearch(uuid),
    refetchInterval: (q) => {
      const d = q.state.data;
      const status = d?.status;
      if (!status) return 3000;
      // Keep polling while the per-location scorecards are still collecting,
      // even though the search itself is "complete" — their statuses advance
      // in the background and we want the candidate progress to tick live.
      if (d && d.scorecards_created > 0 && d.scorecards_complete < d.scorecards_created) {
        return 5000;
      }
      // Otherwise stop at a terminal state OR review (waiting on the user).
      if (TERMINAL.has(status)) return false;
      if (status === 'review') return false;
      return 3000;
    },
  });

  const candidates: ChainSearchCandidate[] = useMemo(() => data?.candidates ?? [], [data]);

  // Server-side selection is the source of truth UNTIL the user changes selection
  // locally; once they do, localSelection wins for this page session.
  const selectedSet = useMemo(() => {
    if (localSelection !== null) return localSelection;
    return new Set(candidates.filter((c) => c.selected).map((c) => c.google_place_id));
  }, [localSelection, candidates]);

  const filtered = useMemo(() => {
    if (!filteringText.trim()) return candidates;
    const q = filteringText.toLowerCase();
    return candidates.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.formatted_address ?? '').toLowerCase().includes(q),
    );
  }, [candidates, filteringText]);

  const selectMut = useMutation({
    mutationFn: () =>
      selectChainCandidates(uuid, Array.from(selectedSet)),
    onSuccess: () => {
      setLocalSelection(null);
      qc.invalidateQueries({ queryKey: ['chain-search', uuid] });
    },
  });

  const createMut = useMutation({
    mutationFn: async () => {
      // Save selection first if dirty, then create
      if (localSelection !== null) {
        await selectChainCandidates(uuid, Array.from(selectedSet));
      }
      return createChainScorecards(uuid);
    },
    onSuccess: () => {
      setLocalSelection(null);
      qc.invalidateQueries({ queryKey: ['chain-search', uuid] });
    },
  });

  const cancelMut = useMutation({
    mutationFn: () => cancelChainSearch(uuid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chain-search', uuid] }),
  });

  const retryMut = useMutation({
    mutationFn: () => retryChainSearch(uuid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chain-search', uuid] }),
  });

  // Cloudscape Table wants the actual item objects in selectedItems, not
  // just keys. Derive them from `selectedSet` (the source of truth for
  // which google_place_ids are picked) and the current candidates list.
  // Must be declared before any early returns to keep React's hooks order
  // stable across renders.
  const selectedItems = useMemo(
    () => candidates.filter((c) => selectedSet.has(c.google_place_id)),
    [candidates, selectedSet],
  );

  if (isLoading) {
    return (
      <DashboardViewLayout
        breadcrumbs={[{ text: 'Chains', href: '/dashboard/chains' }]}
        header={<Header variant="h1">Loading…</Header>}
      >
        <Container />
      </DashboardViewLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardViewLayout
        breadcrumbs={[{ text: 'Chains', href: '/dashboard/chains' }]}
        header={<Header variant="h1">Chain search</Header>}
      >
        <Alert type="error" header="Failed to load">
          {String(error)}
        </Alert>
      </DashboardViewLayout>
    );
  }

  const inFlight = !TERMINAL.has(data.status) && data.status !== 'review';
  const canSelect = data.status === 'review';
  const canCreate = data.status === 'review' && selectedSet.size > 0;

  const handleSelectionChange = (items: ChainSearchCandidate[]) => {
    setLocalSelection(new Set(items.map((c) => c.google_place_id)));
  };

  return (
    <DashboardViewLayout
      breadcrumbs={[
        { text: 'Chains', href: '/dashboard/chains' },
        ...(data.chain_uuid
          ? [{ text: data.chain_name_resolved ?? data.chain_name, href: `/dashboard/chains/${data.chain_uuid}` }]
          : []),
        { text: `Search: ${data.chain_name}`, href: `/dashboard/chains/searches/${uuid}` },
      ]}
      header={
        <Header
          variant="h1"
          description={`Created ${new Date(data.created_at).toLocaleString()}${
            data.completed_at ? ` · Completed ${new Date(data.completed_at).toLocaleString()}` : ''
          }`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => refetch()}>Refresh</Button>
              {inFlight ? (
                <Button loading={cancelMut.isPending} onClick={() => cancelMut.mutate()}>
                  Cancel
                </Button>
              ) : null}
              {data.status === 'failed' ? (
                <Button
                  variant="primary"
                  loading={retryMut.isPending}
                  onClick={() => retryMut.mutate()}
                >
                  Retry
                </Button>
              ) : null}
            </SpaceBetween>
          }
        >
          {data.chain_name} — {data.region_type}
          {data.region_value ? `: ${data.region_value}` : ''}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Container header={<Header variant="h2">Status</Header>}>
          <ColumnLayout columns={4} variant="text-grid">
            <KeyValuePairs
              items={[
                {
                  label: 'State',
                  value: (
                    <StatusIndicator type={STATUS_INDICATOR[data.status] ?? 'in-progress'}>
                      {data.status}
                    </StatusIndicator>
                  ),
                },
              ]}
            />
            <KeyValuePairs
              items={[
                {
                  label: 'Grid points',
                  value: `${data.grid_points_searched} / ${data.grid_points_total}`,
                },
              ]}
            />
            <KeyValuePairs
              items={[{ label: 'Locations found', value: String(data.locations_found) }]}
            />
            <KeyValuePairs
              items={[
                {
                  label: 'Scorecards done',
                  value: `${data.scorecards_complete} / ${data.scorecards_created}`,
                },
              ]}
            />
          </ColumnLayout>
          {data.error_message ? (
            <Box padding={{ top: 'm' }}>
              <Alert type="error" header="Error">
                <Box variant="code">{data.error_message}</Box>
              </Alert>
            </Box>
          ) : null}
        </Container>

        {data.status === 'discovering' || data.status === 'pending' ? (
          <Alert type="info" header="Discovery in progress">
            Searching {data.grid_points_total || '…'} grid points. Results will appear here when
            done.
          </Alert>
        ) : null}

        {data.chain_uuid && data.status === 'review' ? (
          <Alert type="info" header="Adding locations to an existing chain">
            This search is targeted at{' '}
            <Link
              href={`/dashboard/chains/${data.chain_uuid}`}
              onFollow={(e) => {
                e.preventDefault();
                navigate({
                  to: '/dashboard/chains/$chainUuid',
                  params: { chainUuid: data.chain_uuid! },
                });
              }}
            >
              {data.chain_name_resolved ?? data.chain_name}
            </Link>
            . Locations already in the chain were excluded from these results. Tick the
            checkbox on each location to add, then click Create scorecards — selected
            locations join the chain with a scorecard each.
          </Alert>
        ) : null}

        {data.status === 'review' || data.status === 'creating' || data.status === 'complete' ? (
          <Table
            // Embedded: this Table is a top-level section in the page
            // SpaceBetween, sitting next to other <Container>s. Using
            // `variant="container"` doubles the chrome (Cloudscape's
            // default for a top-level table renders its own Container,
            // which on top of the SpaceBetween produces the heavy
            // double-padded look the user flagged). `embedded` strips
            // the inner chrome and lets the table header sit cleanly
            // on the page.
            variant="embedded"
            items={filtered}
            stickyHeader
            // Native Cloudscape multi-select: header gets a "select all on
            // current filter" checkbox automatically, each row gets its own
            // checkbox, keyboard nav works, selection survives row re-orders.
            // trackBy keys on google_place_id so selection is stable across
            // refetches (the API objects re-instantiate but the place_ids
            // don't change).
            selectionType={canSelect ? 'multi' : undefined}
            selectedItems={selectedItems}
            trackBy="google_place_id"
            onSelectionChange={({ detail }) => handleSelectionChange(detail.selectedItems)}
            ariaLabels={{
              selectionGroupLabel: 'Candidate selection',
              allItemsSelectionLabel: () =>
                selectedSet.size === candidates.length && candidates.length > 0
                  ? 'Deselect all candidates'
                  : 'Select all candidates',
              itemSelectionLabel: (_data, c) =>
                selectedSet.has(c.google_place_id)
                  ? `Deselect ${c.name}`
                  : `Select ${c.name}`,
            }}
            empty={
              <Box textAlign="center" color="inherit">
                <b>No candidates</b>
                <Box variant="p" color="inherit">
                  Discovery completed but no locations were found.
                </Box>
              </Box>
            }
            filter={
              <TextFilter
                filteringText={filteringText}
                filteringPlaceholder="Filter by name or address"
                onChange={({ detail }) => setFilteringText(detail.filteringText)}
                countText={`${filtered.length} match${filtered.length === 1 ? '' : 'es'}`}
              />
            }
            header={
              <Header
                variant="h2"
                counter={
                  selectedSet.size > 0
                    ? `(${selectedSet.size}/${candidates.length})`
                    : `(${candidates.length})`
                }
                description={
                  canSelect
                    ? 'Pick which locations to create scorecards for, then click Create scorecards.'
                    : data.status === 'creating'
                      ? 'Creating scorecards…'
                      : 'Scorecards created — links below.'
                }
                actions={
                  canSelect ? (
                    <SpaceBetween direction="horizontal" size="xs">
                      {localSelection !== null && (
                        <Button
                          onClick={() => selectMut.mutate()}
                          loading={selectMut.isPending}
                        >
                          Save selection
                        </Button>
                      )}
                      <Button
                        variant="primary"
                        iconName="add-plus"
                        onClick={() => createMut.mutate()}
                        loading={createMut.isPending}
                        disabled={!canCreate}
                      >
                        Create scorecards{selectedSet.size > 0 ? ` (${selectedSet.size})` : ''}
                      </Button>
                    </SpaceBetween>
                  ) : null
                }
              >
                Candidates
              </Header>
            }
            columnDefinitions={[
              {
                id: 'location',
                header: 'Location',
                minWidth: 260,
                // Same shape as the dashboard "Place" cell: name (links to the
                // scorecard once it exists) + address + the task-driven status
                // indicator. No raw place_id — the dashboards don't show it.
                cell: (c) => (
                  <Header
                    variant="h3"
                    description={
                      <SpaceBetween direction="vertical" size="xxxs">
                        <Box variant="span" color="text-body-secondary">
                          {c.formatted_address ?? '—'}
                        </Box>
                        {c.created_report_tasks && c.created_report_tasks.length > 0 ? (
                          <PlaceStatus tasks={c.created_report_tasks} />
                        ) : c.created_report_id ? (
                          <StatusIndicator type="pending">Queued</StatusIndicator>
                        ) : null}
                      </SpaceBetween>
                    }
                  >
                    {c.created_report ? (
                      <Link href={`/${c.created_report.slug ?? c.created_report.uuid}`} external>
                        {c.name}
                      </Link>
                    ) : (
                      c.name
                    )}
                  </Header>
                ),
              },
              {
                id: 'periods_google',
                header: 'Google Scores',
                // Identical to the /dashboard/scorecard "Google Scores" column.
                // Empty until Google stats land — which is exactly when the
                // scorecard becomes viewable (no "ready but blank" state).
                cell: (c) =>
                  c.created_report ? (
                    <PlacePeriodList report={c.created_report} name={c.name} source="google" />
                  ) : (
                    <Box color="text-status-inactive">—</Box>
                  ),
              },
              {
                id: 'periods_yelp',
                header: 'Yelp Scores',
                cell: (c) =>
                  c.created_report ? (
                    <PlacePeriodList report={c.created_report} name={c.name} source="yelp" />
                  ) : (
                    <Box color="text-status-inactive">—</Box>
                  ),
              },
            ]}
          />
        ) : null}

        {data.notes ? (
          <Container header={<Header variant="h2">Notes</Header>}>
            <Box variant="p">{data.notes}</Box>
          </Container>
        ) : null}
      </SpaceBetween>
    </DashboardViewLayout>
  );
}
